import { TransferState } from '@angular/core';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { Observable, firstValueFrom } from 'rxjs';

import {
  ApiService,
  IOutletCalendarDay,
  IDeliveryCalendarDay,
  scheduleHelper,
  TransferableGetApiService,
  IOutletDelivery, IScheduleOverride
} from '@cakeiteasy/web-utils';

import { IBakeryDeliveryInfoOriginal } from '../../delivery/models/i-bakery-delivery.info';
import { ICustomDeliveryTime } from '../../cart/models/delivery/i-cart-delivery-info';
import { DeliveryFeePayload, IThirdPartyDelivery, ThirdPartyLocation } from '../../delivery/models';

@Injectable({ providedIn: 'root' })
export class DeliveryApiService extends TransferableGetApiService {
  constructor(@Inject('AppConfig') appConfig: any,
              @Inject(PLATFORM_ID) _platformId: any,
              api: ApiService,
              transferState: TransferState) {
    super(api, appConfig, _platformId, transferState);
  }

  public getDeliveryInfo(bakeryId: number): Observable<IBakeryDeliveryInfoOriginal> {
    return this.api
      .get('api.bakery', 'delivery_info', { id: bakeryId });
  }

  public getOutletsByBakeryId(bakeryId: number, params?: any): Promise<IOutletDelivery[]> {
    return firstValueFrom(this.getWithCaching('api.bakery', 'outlets',
      { id: bakeryId }, { params }))
      .then((outlets) => outlets.map(outlet => this._extendOutlet(outlet)));
  }

  public getOutletsByRefName(refName: string) {
    return firstValueFrom(this.getWithCaching('api.outlet', 'outlets', {}, { params: { refName } }))
      .then((outlets) => outlets.map(outlet => this._extendOutlet(outlet)));
  }

  public getOutletsByBakeryIds(bakeryIds: number[], locationName?: string) {
    const queryParams = {
      bakeryIds
    };

    if (locationName) {
      Object.assign(queryParams, { locationName });
    }

    return firstValueFrom(this.getWithCaching('api.bakery', 'bakeries_outlets', null, {
      params: queryParams
    }))
      .then((outlets) => outlets.map(outlet => this._extendOutlet(outlet)));
  }

  public getDeliveryByLocation(bakeryIds: number[], postNumber: string, locationName: string) {
    return firstValueFrom(this.getWithCaching('api.bakery', 'delivery_by_location', null, {
      params: {
        bakeryIds, postNumber, locationName
      }
    }));
  }

  public getDeliveryScheduleOverrides(bakeryId: number, postNumber: string): Observable<ICustomDeliveryTime[]> {
    return this.api
      .get('api.bakery', 'delivery_schedule_overrides',
        { pk: bakeryId, 'post-code': postNumber },
      );
  }

  public getOutletScheduleOverrides(bakeryId: number, outletId: number): Observable<IScheduleOverride[]> {
    return this.api
      .get('api.bakery', 'outlet_schedule_overrides',
        { pk: bakeryId, outlet_id: outletId },
      );
  }

  public getBakeryDeliveryPrice(bakeryId: number, postNumber: string): Observable<any[]> {
    return this.api.get('api.bakery', 'delivery_prices',
      { bakeryId },
      { params: { postNumber } });
  }

  /** Probably, this method is useless, it should be investigated*/
  public getUntiedOutletCalendar(outletId: number,
                                 year: number, month: number): Promise<any[]> {
    return firstValueFrom(this.api.get('api.outlet', 'outlet_calendar',
      null, {
        params: {
          outlet: outletId,
          year,
          month
        }
      },));
  }

  public getOutletCalendar(bakeryId: number, outletId: number,
                           year: number, month: number): Promise<IOutletCalendarDay[]> {
    return firstValueFrom(this.api.get('api.cart', 'outlet_calendar',
      { bakeryId }, {
        params: { outlet: outletId, year, month }
      }));
  }

  public getOrderOutletCalendar(orderId: number, outletId: number,
                                year: number, month: number): Promise<IOutletCalendarDay[]> {
    return firstValueFrom(this.api.get('api.orders', 'outlet_calendar',
      { orderId }, {
        params: { outlet: outletId, year, month }
      }));
  }

  public getDeliveryCalendar(bakeryId: number, year: number, month: number, postNumber: string): Promise<IDeliveryCalendarDay[]> {
    return firstValueFrom(this.api.get('api.cart', 'delivery_calendar',
      { bakeryId }, {
        params: { year, month, postNumber }
      }));
  }

  public getOrderDeliveryCalendar(orderId: number, year: number, month: number, postNumber: string): Promise<IDeliveryCalendarDay[]> {
    const ignoreWeekends = 'true';
    return firstValueFrom(this.api.get('api.orders', 'delivery_calendar',
      { orderId }, {
        params: { year, month, postNumber, ignoreWeekends }
      }));
  }

  getExternalDeliveryFee(body: DeliveryFeePayload): Observable<IThirdPartyDelivery> {
    return this.api.post('api.cart', 'delivery_fee', null, body);
  }

  getExternalDeliveryCalendar(params: { year: number, month: number, outletId?: number }): Promise<IDeliveryCalendarDay> {
    return firstValueFrom(this.api.get('api.cart', 'external_calendar', null, { params }));
  }

  getExternalDeliveryAvailableTime(body: {
    dropoffLocation: ThirdPartyLocation,
    scheduledDropoffDate: string,
    outlet: number
  }): Observable<{
    id: number,
    schedule: string[]
  }> {
    return this.api.post('api.cart', 'available_time', null, body);
  }

  private _extendOutlet(outlet) {
    outlet.formattedWorkingDays = scheduleHelper.getOutletSchedule(outlet.workingDays);
    outlet.formattedPickupSchedule = scheduleHelper.getOutletSchedule(outlet.pickupSchedule);
    return outlet;
  }
}
